import classNames from 'clsx';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import MultipleSelect from '@swf/components/Inputs/components/MultipleSelect/MultipleSelect';
import type { OptionToggle } from '@swf/components/Inputs/components/MultipleSelect/MultipleSelectToggle/MultipleSelectToggle';
import { select } from '@swf/core/store';
import type { CustomRangePayload } from '@swf/poker';
import { FilterName } from '@swf/poker';

import { usePokerMultiTableTournamentsWidget } from '../../model';
import {
  isCustomFilterOption,
  isGroupFilterOption,
  mapCustomRangeVariables,
  mapFilterVariables
} from '../../utils/filters';

import BuyInDescription from './BuyInDescription/BuyInDescription';
import CustomRangeBuyIn from './CustomRangeBuyIn/CustomRangeBuyIn';
import { TXTCLI_All } from './FiltersBar.messages.json';
import styles from './FiltersBar.scss';

const DEFAULT_OPTION = { label: TXTCLI_All, value: 'all' };
const TOGGLE_CLASSES = { wrapper: styles.optionWrapper, toggle: styles.option };

const getDefaultOption = (filterName: FilterName) => ({
  ...DEFAULT_OPTION,
  id: filterName + '-all'
});

const getFilterClasses = (hasGroups: boolean) => ({
  ...styles,
  container: classNames(styles.container, {
    [styles.containerGroup]: hasGroups
  }),
  dropdownOpen: classNames(styles.dropdownOpen, {
    [styles.dropdownOpenGroup]: hasGroups
  }),
  optionsWrapper: hasGroups ? styles.optionsWrapperGroup : undefined,
  optionsWrapperOpen: hasGroups ? styles.optionsWrapperOpenGroup : undefined,
  optionsList: hasGroups ? styles.optionsListGroup : undefined
});

const FiltersBar: React.FC = () => {
  const { widgetSelect, widgetDispatch } = usePokerMultiTableTournamentsWidget();
  const filtersData = useSelector(widgetSelect.getFiltersData);
  const playMode = useSelector(select.currency.getPlayMode);

  const onFilterSelected = useCallback(
    (id: string, options: OptionToggle[]) => {
      const variables = mapFilterVariables(id as FilterName, options, playMode);
      widgetDispatch.setActiveFilters(variables);
    },
    [widgetDispatch, playMode]
  );

  const onCustomRangeSelected = useCallback(
    (name: FilterName, range: CustomRangePayload) => {
      const variables = mapCustomRangeVariables(name, range);
      widgetDispatch.setActiveFilters(variables);
    },
    [widgetDispatch]
  );

  return (
    <div className={styles.filtersBar} data-testid="filters-bar">
      {filtersData.map((filter) => (
        <MultipleSelect
          id={filter.name}
          title={filter.title}
          defaultOption={getDefaultOption(filter.name)}
          onSelect={onFilterSelected}
          key={filter.name}
          dataTestId={`mtt-${filter.name}-filter-select`}
          trackingLabel={filter.title}
          classes={getFilterClasses(filter.hasGroups)}
          showNumberLabel={filter.name !== FilterName.BuyIn}
        >
          {filter.options.map((option) => {
            const { id, label } = option;
            const toggleProps = { id, label, classes: TOGGLE_CLASSES };

            if (isGroupFilterOption(option)) {
              const props = {
                ...toggleProps,
                options: option.options,
                classes: { ...TOGGLE_CLASSES, toggleGroup: styles.optionGroupToggle }
              };
              return <MultipleSelect.GroupToggle {...props} key={id} />;
            } else if (isCustomFilterOption(option)) {
              return (
                <MultipleSelect.CustomRange
                  {...toggleProps}
                  filterId={filter.name}
                  value={option.value}
                  key={id}
                >
                  <CustomRangeBuyIn
                    toggleId={id}
                    filterId={filter.name}
                    onSelect={onCustomRangeSelected}
                  />
                </MultipleSelect.CustomRange>
              );
            } else {
              let description;
              if (filter.name === FilterName.BuyIn) {
                toggleProps.classes.wrapper = classNames(styles.optionWrapper, styles.description);
                description = <BuyInDescription optionValue={option.value} />;
              }

              return (
                <MultipleSelect.Toggle
                  description={description}
                  value={option.value}
                  key={id}
                  {...toggleProps}
                />
              );
            }
          })}
        </MultipleSelect>
      ))}
    </div>
  );
};

export default FiltersBar;
